/* Buscador y datos de atletas para la Ficha de Atleta.
 *
 * Reutiliza los shards PB/SB que el workflow ya sincroniza al /public de
 * podios-stories (agg_pbsb_<LETRA>.json), keyed por inicial del nombre.
 * No añade datasets nuevos: cada ficha es {atleta, fechaNac, pais,
 * ultimaTemporada, pb{prueba→valor}, sb{prueba→valor}} — sin club, sin
 * fechas ni histórico (de ahí que la ficha solo muestre PB o SB).
 */
import { isHigherBetter, isBetterMark } from '@/lib/marks';
import { stripDiacritics } from '@/lib/normalize';
import { categoriaPorEdad, anioDeFecha } from '@/lib/podios/cruce';
import { TEMPORADA_DEFECTO_YEAR } from '@/lib/podios/data';

export interface AtletaPbSb {
  atleta: string;
  fechaNac: string;
  pais: string;
  ultimaTemporada: string;
  pb: Record<string, number>;
  sb: Record<string, number>;
}

function norm(s: string): string {
  return stripDiacritics((s || '').toLowerCase()).trim();
}

function shardLetter(s: string): string {
  const c = norm(s).toUpperCase().charAt(0);
  return c >= 'A' && c <= 'Z' ? c : '#';
}

// Cache de shards ya descargados (por letra).
const _cache = new Map<string, AtletaPbSb[]>();

async function fetchShard(letter: string): Promise<AtletaPbSb[]> {
  const cached = _cache.get(letter);
  if (cached) return cached;
  try {
    const r = await fetch(`/agg_pbsb_${letter}.json`);
    if (!r.ok) {
      _cache.set(letter, []);
      return [];
    }
    // El shard es { generatedAt, letra, temporadaActual, atletas: [...] }.
    const data = (await r.json()) as { atletas?: AtletaPbSb[] } | AtletaPbSb[];
    const arr = Array.isArray(data) ? data : (data.atletas ?? []);
    _cache.set(letter, arr);
    return arr;
  } catch {
    _cache.set(letter, []);
    return [];
  }
}

/** Combina los mapas pb/sb de un registro duplicado en otro, quedándose con
 *  la mejor marca de cada prueba. */
function mergePbSb(target: Record<string, number>, src: Record<string, number> | undefined): void {
  for (const [ev, val] of Object.entries(src || {})) {
    if (target[ev] == null || isBetterMark(ev, val, target[ev])) target[ev] = val;
  }
}

/** Fusiona registros duplicados del mismo atleta (mismo nombre normalizado +
 *  fecha de nacimiento). El origen a veces parte un atleta en dos fichas
 *  (p.ej. una con relevos y otra con pruebas individuales). */
function dedupe(list: AtletaPbSb[]): AtletaPbSb[] {
  const map = new Map<string, AtletaPbSb>();
  for (const a of list) {
    const key = `${norm(a.atleta)}|${a.fechaNac || ''}`;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, { ...a, pb: { ...a.pb }, sb: { ...a.sb } });
      continue;
    }
    mergePbSb(prev.pb, a.pb);
    mergePbSb(prev.sb, a.sb);
    if ((a.ultimaTemporada || '') > (prev.ultimaTemporada || '')) prev.ultimaTemporada = a.ultimaTemporada;
    // Nombre: nos quedamos con el de capitalización "normal" (no todo mayúsculas).
    if (a.atleta !== a.atleta.toUpperCase() && prev.atleta === prev.atleta.toUpperCase()) {
      prev.atleta = a.atleta;
    }
  }
  return [...map.values()];
}

/** Busca atletas cuyo nombre contiene `query`. Carga el shard de la inicial
 *  del query (los shards están keyed por inicial del nombre, igual que la
 *  búsqueda por nombre de pila de la web de rankings). */
export async function buscarAtletas(query: string, limit = 25): Promise<AtletaPbSb[]> {
  const q = norm(query);
  if (q.length < 2) return [];
  const shard = await fetchShard(shardLetter(q));
  const matches = shard.filter((a) => norm(a.atleta).includes(q));
  const merged = dedupe(matches);
  merged.sort((a, b) => {
    const sa = norm(a.atleta).startsWith(q) ? 0 : 1;
    const sb = norm(b.atleta).startsWith(q) ? 0 : 1;
    return sa - sb || a.atleta.localeCompare(b.atleta);
  });
  return merged.slice(0, limit);
}

/** Pruebas con marca (unión de las claves de pb y sb), ordenadas. */
export function pruebasDeAtleta(a: AtletaPbSb): string[] {
  const set = new Set<string>([...Object.keys(a.pb || {}), ...Object.keys(a.sb || {})]);
  return [...set].sort();
}

/** Categoría por edad (Sub 20, Absoluta, Master…) a partir de fechaNac. */
export function categoriaDeAtleta(a: AtletaPbSb): string | null {
  return categoriaPorEdad(anioDeFecha(a.fechaNac), TEMPORADA_DEFECTO_YEAR);
}

const COMBINADAS = /pentatl|heptatl|decatl|triatl|biatl|tetral/;

/** Formatea un valor numérico del aggregate como marca legible según la
 *  prueba: campo → metros (2 dec), carrera → segundos o m:ss.cc, combinada
 *  → puntos enteros. Es el inverso de parseMarkToNum. */
export function formatMarca(prueba: string, v: number | undefined | null): string {
  if (v == null || !Number.isFinite(v)) return '';
  const p = (prueba || '').toLowerCase();
  if (COMBINADAS.test(p)) return String(Math.round(v));
  if (isHigherBetter(prueba)) return v.toFixed(2);
  // Carrera (tiempo en segundos).
  if (v < 60) return v.toFixed(2);
  const totalCs = Math.round(v * 100);
  const cs = totalCs % 100;
  const totalS = Math.floor(totalCs / 100);
  const s = totalS % 60;
  const totalM = Math.floor(totalS / 60);
  const m = totalM % 60;
  const h = Math.floor(totalM / 60);
  const ss = `${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Etiqueta de prueba para mostrar (clave canónica → mayúsculas legibles). */
export function labelPrueba(clave: string): string {
  return (clave || '').toUpperCase();
}
